import { useState, useEffect } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  MessageCircle,
  Settings,
  User,
  Menu,
  Search,
  ArrowLeft,
  Bell
} from 'lucide-react';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useChats } from '@/hooks/mensag/useChats';
import { useContactRequests } from '@/hooks/mensag/useContacts';
import { ChatListItem } from './ChatListItem';
import { MenuMovil } from './MenuMovil';
import { Input } from '@/components/ui/input';
import { useContactRequests } from '@/hooks/mensag/useContacts';

export function MensagLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const navigate = useNavigate();

  // Auto-close mobile menu on navigation
  useEffect(() => {
    const handleLocationChange = () => setMobileMenuOpen(false);
    window.addEventListener('popstate', handleLocationChange);
    return () => window.removeEventListener('popstate', handleLocationChange);
  }, []);

  const { user } = useCurrentUser();
  const { data: chats, isLoading } = useChats();
  const { data: contactRequests } = useContactRequests();

  // Filter chats based on search query
  const filteredChats = chats?.filter(chat =>
    chat.contactName.toLowerCase().includes(searchQuery.toLowerCase()) ||
    chat.lastMessage?.toLowerCase().includes(searchQuery.toLowerCase())
  ) || [];

  const pendingRequestsCount = contactRequests?.filter(r => r.direction === 'received' && r.status === 'pending').length || 0;

  return (
    <div className="flex h-screen bg-gray-50 dark:bg-gray-900">
      {/* Mobile Menu Drawer */}
      <MenuMovil
        isOpen={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
      />

      {/* Desktop Sidebar */}
      <div className={cn(
        "hidden md:flex w-80 bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 flex-col transition-all duration-300",
        !sidebarOpen && "md:w-16"
      )}>
        {/* Header */}
        <div className={cn(
          "p-3 md:p-4 border-b border-gray-200 dark:border-gray-700",
          !sidebarOpen && "md:p-4"
        )}>
          <div className="flex items-center justify-between mb-3 md:mb-4">
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center">
                <MessageCircle className="h-5 w-5 text-white" />
              </div>
              <h1 className={cn(
                "text-xl font-bold text-gray-900 dark:text-white",
                !sidebarOpen && "md:inline hidden"
              )}>MensaG</h1>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="md:hidden"
            >
              <ArrowLeft className={cn(
                "h-4 w-4 transition-transform",
                !sidebarOpen && "rotate-180"
              )} />
            </Button>
          </div>

          {/* Search - hidden when sidebar collapsed */}
          {(sidebarOpen) && (
            <div className={cn("relative")}>
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Buscar conversaciones..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 bg-gray-50 dark:bg-gray-700 h-9 text-sm"
              />
            </div>
          )}
        </div>

        {/* Chats List */}
        <div className="flex-1 overflow-y-auto">
          {isLoading ? (
            <div className="p-6 space-y-4">
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-gray-200 rounded-full animate-pulse" />
                  <div className="flex-1">
                    <div className="h-4 bg-gray-200 rounded animate-pulse mb-2" />
                    <div className="h-3 bg-gray-200 rounded animate-pulse w-2/3" />
                  </div>
                </div>
              ))}
            </div>
          ) : filteredChats.length === 0 ? (
            <div className="p-8 text-center">
              <MessageCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500 dark:text-gray-400">
                {searchQuery ? 'No se encontraron conversaciones' : 'No tienes conversaciones aún'}
              </p>
            </div>
          ) : (
            <div className="divide-y divide-gray-200 dark:divide-gray-700">
              {filteredChats.map((chat) => (
                <ChatListItem
                  key={chat.id}
                  chat={chat}
                  onClick={() => navigate(`/chat/${chat.contactId}`)}
                />
              ))}
            </div>
          )}
        </div>

        {/* User Profile */}
        {user && (
          <div className={cn(
            "border-t border-gray-200 dark:border-gray-700",
            (!sidebarOpen && window.matchMedia('(min-width: 768px)').matches) ? "p-2" : "p-3 md:p-4"
          )}>
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2 md:space-x-3">
                <Avatar className="h-8 w-8 flex-shrink-0">
                  <AvatarImage src={user.profile?.picture} />
                  <AvatarFallback className="text-xs">
                    {user.profile?.name?.charAt(0) || user.pubkey.slice(0, 2)}
                  </AvatarFallback>
                </Avatar>
                {(sidebarOpen) && (
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                      {user.profile?.name || 'Tú'}
                    </p>
                    <p className="text-xs text-gray-500 truncate">
                      En línea
                    </p>
                  </div>
                )}
              </div>
              <div className="flex items-center space-x-1">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setSidebarOpen(!sidebarOpen);
                  }}
                  className="p-1.5 h-8 w-8"
                >
                  <User className="h-4 w-4" />
                </Button>
                {(sidebarOpen) && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => navigate('/settings')}
                    className="p-1.5 h-8 w-8"
                  >
                    <Settings className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col relative min-w-0">
        {/* Mobile Header */}
        <div className="sticky top-0 z-30 md:hidden flex items-center justify-between p-3 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setMobileMenuOpen(true)}
            className="p-2"
          >
            <Menu className="h-5 w-5" />
          </Button>
          <div className="flex items-center space-x-2">
            <div className="w-7 h-7 bg-blue-500 rounded-full flex items-center justify-center">
              <MessageCircle className="h-4 w-4 text-white" />
            </div>
            <h1 className="text-lg font-bold text-gray-900 dark:text-white">MensaG</h1>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="p-2 relative"
          >
            <Bell className="h-5 w-5" />
            {pendingRequestsCount > 0 && (
              <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center text-white font-medium">
                {pendingRequestsCount > 9 ? '9+' : pendingRequestsCount}
              </span>
            )}
          </Button>
        </div>

        {/* Page Content */}
        <div className="flex-1 min-h-0 overflow-hidden">
          <Outlet />
        </div>
      </div>
    </div>
  );
}